import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationApi, campaignApi } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

interface ApplicationPageProps {
  applicant: any;
  onUpdate: () => Promise<void>;
}

const ApplicationPage: React.FC<ApplicationPageProps> = ({ applicant, onUpdate }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const confirm = useConfirm();

  const [form, setForm] = useState({
    name: '',
    phone: '',
    position: '',
    essay: '',
  });
  const [resumeFile, setResumeFile] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [campaignPositions, setCampaignPositions] = useState<any[]>([]);

  // 최초 로드 시에만 applicant 데이터로 폼 초기화
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (applicant && !initialized) {
      setForm({
        name: applicant.name || '',
        phone: applicant.phone || '',
        position: applicant.position || '',
        essay: applicant.essay || '',
      });
      setResumeFile(applicant.resume_url || null);
      setInitialized(true);
    }
  }, [applicant, initialized]);

  // 캠페인 포지션 목록 로드
  useEffect(() => {
    if (applicant?.campaign_id) {
      campaignApi.getCampaignPositions(applicant.campaign_id)
        .then((data) => {
          const positions = data.positions || data || [];
          setCampaignPositions(positions);
        })
        .catch(() => {
          setCampaignPositions([]);
        });
    }
  }, [applicant?.campaign_id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await applicationApi.save({
        name: form.name,
        phone: form.phone,
        position: form.position,
        essay: form.essay,
      });
      // save 응답으로 폼 유지 (onUpdate가 폼을 초기화하는 문제 방지)
      setForm({
        name: saved.name || form.name,
        phone: saved.phone || form.phone,
        position: saved.position || form.position,
        essay: saved.essay || form.essay,
      });
      setResumeFile(saved.resume_url || resumeFile);
      await onUpdate();
      toast.success('임시저장 되었습니다.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('PDF 파일만 업로드 가능합니다.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const data = await applicationApi.uploadResume(file);
      setResumeFile(data.resume_url);
      await onUpdate();
      toast.success('이력서가 업로드되었습니다.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('이름을 입력해주세요.'); return; }
    if (!form.phone.trim()) { toast.error('연락처를 입력해주세요.'); return; }
    if (!resumeFile) { toast.error('이력서를 업로드해주세요.'); return; }
    if (!form.essay.trim()) { toast.error('에세이를 작성해주세요.'); return; }
    if (form.essay.trim().length < 200) { toast.error('에세이는 최소 200자 이상 작성해주세요.'); return; }
    if (form.essay.trim().length > 3000) { toast.error('에세이는 3000자 이내로 작성해주세요.'); return; }

    const ok = await confirm({
      title: '지원서를 제출하시겠습니까?',
      message: '제출 후에는 수정할 수 없습니다.',
      confirmText: '제출',
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      // 먼저 최신 내용 저장
      await applicationApi.save({
        name: form.name,
        phone: form.phone,
        position: form.position,
        essay: form.essay,
      });
      // 제출
      await applicationApi.submit();
      await onUpdate();
      navigate('/');
    } catch (err: any) {
      toast.error(err.response?.data?.message || '제출에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitted = applicant?.is_submitted;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* 헤더 */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-base font-semibold text-slate-800">입사지원서</h1>
          </div>
          {!isSubmitted && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50"
            >
              {saving ? '저장 중...' : '임시저장'}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 md:py-8 space-y-6">
        {isSubmitted && (
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl text-sm text-indigo-700">
            지원서가 제출되었습니다. 수정할 수 없습니다.
          </div>
        )}

        {/* 기본 정보 */}
        <section className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">기본 정보</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">이름 <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="name"
                value={form.name}
                disabled
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">연락처 <span className="text-red-500">*</span></label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                disabled
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">
                지원 포지션 {campaignPositions.length > 0 && <span className="text-red-500">*</span>}
              </label>
              {campaignPositions.length > 0 ? (
                <select
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                  disabled={isSubmitted}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500 bg-white"
                >
                  <option value="">포지션을 선택해주세요</option>
                  {campaignPositions.map((pos: any) => (
                    <option key={pos.id} value={pos.name}>{pos.full_name || pos.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                  disabled={isSubmitted}
                  placeholder="예: 컨설턴트, 회계사, 인턴 등"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500"
                />
              )}
            </div>
          </div>
        </section>

        {/* 이력서 */}
        <section className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">이력서 <span className="text-red-500">*</span></h2>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          {resumeFile ? (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">이력서 업로드 완료</p>
                  <p className="text-xs text-slate-400">{resumeFile.split('/').pop()}</p>
                </div>
              </div>
              {!isSubmitted && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  {uploading ? '업로드 중...' : '변경'}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || isSubmitted}
              className="w-full p-6 border-2 border-dashed border-slate-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-center">
                {uploading ? (
                  <svg className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-slate-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                )}
                <p className="text-sm text-slate-600 font-medium">
                  {uploading ? '업로드 중...' : '클릭하여 이력서를 업로드하세요'}
                </p>
                <p className="text-xs text-slate-400 mt-1">PDF 파일만 가능 (최대 10MB)</p>
              </div>
            </button>
          )}
        </section>

        {/* 에세이 */}
        <section className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">에세이 <span className="text-red-500">*</span></h2>
            <span className={`text-xs ${form.essay.length < 200 ? 'text-red-400' : form.essay.length > 3000 ? 'text-red-400' : 'text-slate-400'}`}>{form.essay.length} / 200~3000자</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            GRK Career Management에 지원하게 된 동기와 본인의 강점을 자유롭게 작성해주세요.
          </p>
          <textarea
            name="essay"
            value={form.essay}
            onChange={handleChange}
            disabled={isSubmitted}
            rows={12}
            placeholder="자유롭게 작성해주세요..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y disabled:bg-slate-50 disabled:text-slate-500"
          />
        </section>

        {/* 제출 버튼 */}
        {!isSubmitted && (
          <div className="flex gap-3 pb-8">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {saving ? '저장 중...' : '임시저장'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {submitting ? '제출 중...' : '지원서 제출'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ApplicationPage;
